import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import {
  Gavel, Plus, Pencil, ChevronDown, ChevronUp, Loader2, X,
} from 'lucide-react';

import { decisionsApi, driversApi, projectsApi } from '../api/services';
import type { AnnualStrategicDriver, Decision, Project } from '../types/models';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// DecisionsPage — Phase 3 task 3.8.
//
// Lists every decision on the current user's account and exposes the new
// traceability fields (meeting source, evidence, outcome review date)
// alongside the existing title / rationale / stakeholders / driver / project.
// Each row opens an inline detail card; a "+" button and pencil icon open
// the same create/edit dialog so the page is a full decisions log surface.
// ---------------------------------------------------------------------------

type StatusFilter = 'All' | 'Draft' | 'Final' | 'UnderReview' | 'Revisited';

interface DecisionFormState {
  id: string | null;
  title: string;
  rationale: string;
  stakeholders: string;
  decisionDate: string;
  strategicDriverId: string;
  projectId: string;
  status: string;
  reviewDate: string;
  reviewReason: string;
  meetingSource: string;
  evidence: string;
  outcomeReviewDate: string;
}

const BLANK_FORM: DecisionFormState = {
  id: null,
  title: '',
  rationale: '',
  stakeholders: '',
  decisionDate: format(new Date(), 'yyyy-MM-dd'),
  strategicDriverId: '',
  projectId: '',
  status: 'Final',
  reviewDate: '',
  reviewReason: '',
  meetingSource: '',
  evidence: '',
  outcomeReviewDate: '',
};

const STATUS_FILTERS: StatusFilter[] = ['All', 'Draft', 'Final', 'UnderReview', 'Revisited'];

// Radix Select can't carry an empty SelectItem value, so we round-trip a
// sentinel "none" through the boundary instead of storing it in state.
const NONE = '__none__';

function formatDate(iso: string | undefined): string {
  if (!iso) return '—';
  try { return format(new Date(iso), 'd MMM yyyy'); } catch { return iso; }
}

function prettyStatus(s: StatusFilter | string): string {
  return s === 'All' ? 'All' : s.replace(/([A-Z])/g, ' $1').trim();
}

export default function DecisionsPage() {
  const [decisions, setDecisions] = useState<Decision[]>([]);
  const [drivers, setDrivers] = useState<AnnualStrategicDriver[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('All');
  const [driverFilter, setDriverFilter] = useState<string>('');
  const [outcomeReviewFilter, setOutcomeReviewFilter] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<DecisionFormState>(BLANK_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [decs, drvs, prjs] = await Promise.all([
        decisionsApi.getAll(),
        driversApi.getAll().catch(() => [] as AnnualStrategicDriver[]),
        projectsApi.getAll().catch(() => [] as Project[]),
      ]);
      setDecisions(decs);
      setDrivers(drvs);
      setProjects(prjs);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Could not load decisions.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    return decisions.filter((d) => {
      if (statusFilter !== 'All' && d.status !== statusFilter) return false;
      if (driverFilter && d.strategicDriverId !== driverFilter) return false;
      if (outcomeReviewFilter) {
        if (!d.outcomeReviewDate) return false;
        const due = new Date(d.outcomeReviewDate);
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() + 7);
        if (due > cutoff) return false; // not due in the next 7 days
      }
      return true;
    });
  }, [decisions, statusFilter, driverFilter, outcomeReviewFilter]);

  // Outcome-review reminder counters — drives the "N due" badge above the
  // filter chips so executives notice decisions whose outcome was meant to
  // be reviewed today / this week. Phase 3 §3.8 second slice.
  const outcomeReviewStats = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const sevenDays = new Date(today);
    sevenDays.setDate(today.getDate() + 7);
    let overdue = 0;
    let dueThisWeek = 0;
    for (const d of decisions) {
      if (!d.outcomeReviewDate) continue;
      const due = new Date(d.outcomeReviewDate);
      due.setHours(0, 0, 0, 0);
      if (due < today) overdue++;
      else if (due <= sevenDays) dueThisWeek++;
    }
    return { overdue, dueThisWeek };
  }, [decisions]);

  const driverById = useMemo(() => {
    const m = new Map<string, AnnualStrategicDriver>();
    drivers.forEach((d) => m.set(d.id, d));
    return m;
  }, [drivers]);
  const projectById = useMemo(() => {
    const m = new Map<string, Project>();
    projects.forEach((p) => m.set(p.id, p));
    return m;
  }, [projects]);

  const handleOpenCreate = () => {
    setForm(BLANK_FORM);
    setDialogOpen(true);
  };

  const handleOpenEdit = (d: Decision) => {
    setForm({
      id: d.id,
      title: d.title,
      rationale: d.rationale ?? '',
      stakeholders: d.stakeholders ?? '',
      decisionDate: d.decisionDate.slice(0, 10),
      strategicDriverId: d.strategicDriverId ?? '',
      projectId: d.projectId ?? '',
      status: d.status,
      reviewDate: d.reviewDate?.slice(0, 10) ?? '',
      reviewReason: d.reviewReason ?? '',
      meetingSource: d.meetingSource ?? '',
      evidence: d.evidence ?? '',
      outcomeReviewDate: d.outcomeReviewDate?.slice(0, 10) ?? '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const payload = {
        title: form.title.trim(),
        rationale: form.rationale.trim() || undefined,
        stakeholders: form.stakeholders.trim() || undefined,
        decisionDate: form.decisionDate || undefined,
        strategicDriverId: form.strategicDriverId || undefined,
        projectId: form.projectId || undefined,
        status: form.status,
        reviewDate: form.reviewDate || undefined,
        reviewReason: form.reviewReason.trim() || undefined,
        meetingSource: form.meetingSource.trim() || undefined,
        evidence: form.evidence.trim() || undefined,
        outcomeReviewDate: form.outcomeReviewDate || undefined,
      };
      if (form.id) {
        await decisionsApi.update(form.id, payload);
      } else {
        await decisionsApi.create(payload);
      }
      setDialogOpen(false);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Could not save decision.');
    } finally {
      setSaving(false);
    }
  };

  const hasReviewSignal = outcomeReviewStats.overdue > 0 || outcomeReviewStats.dueThisWeek > 0;

  return (
    <div className="mx-auto max-w-[1200px] p-4 md:p-6">
      <div className="mb-1 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Gavel className="h-7 w-7 text-primary" />
          <h1 className="text-2xl font-semibold tracking-tight">Decisions</h1>
        </div>
        <Button onClick={handleOpenCreate} className="gap-1.5">
          <Plus className="h-4 w-4" />
          Log decision
        </Button>
      </div>
      <p className="mb-6 text-sm text-muted-foreground">
        Every decision recorded against this account, with its rationale, stakeholders, evidence, and review schedule.
      </p>

      {/* Outcome-review reminder strip — surfaces decisions whose
          OutcomeReviewDate is overdue or due in the next 7 days. Suppressed
          when nothing is due so it stays out of the way. */}
      {hasReviewSignal && (
        <Alert
          variant={outcomeReviewStats.overdue > 0 ? 'warning' : 'info'}
          className="mb-4"
        >
          <div className="flex items-center justify-between gap-3">
            <AlertDescription className="flex-1">
              {outcomeReviewStats.overdue > 0 && (
                <strong>{outcomeReviewStats.overdue} overdue</strong>
              )}
              {outcomeReviewStats.overdue > 0 && outcomeReviewStats.dueThisWeek > 0 && ' · '}
              {outcomeReviewStats.dueThisWeek > 0 && (
                <>{outcomeReviewStats.dueThisWeek} due this week</>
              )}
              {' '}— time to review whether the outcome matched what you expected.
            </AlertDescription>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setOutcomeReviewFilter(!outcomeReviewFilter)}
            >
              {outcomeReviewFilter ? 'Show all' : 'Show only these'}
            </Button>
          </div>
        </Alert>
      )}

      {/* Filter bar — status segmented control + driver Select + optional
          outcome-review toggle chip. */}
      <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-center">
        <div
          role="radiogroup"
          aria-label="Status filter"
          className="inline-flex rounded-md border border-border bg-card/40 p-0.5"
          onKeyDown={(e) => {
            const i = STATUS_FILTERS.indexOf(statusFilter);
            if (i < 0) return;
            if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
              e.preventDefault();
              setStatusFilter(STATUS_FILTERS[(i - 1 + STATUS_FILTERS.length) % STATUS_FILTERS.length]);
            } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
              e.preventDefault();
              setStatusFilter(STATUS_FILTERS[(i + 1) % STATUS_FILTERS.length]);
            }
          }}
        >
          {STATUS_FILTERS.map((s) => {
            const checked = statusFilter === s;
            return (
              <Button
                key={s}
                role="radio"
                aria-checked={checked}
                tabIndex={checked ? 0 : -1}
                size="sm"
                variant={checked ? 'default' : 'ghost'}
                onClick={() => setStatusFilter(s)}
                className="h-7 px-3 text-xs"
              >
                {prettyStatus(s)}
              </Button>
            );
          })}
        </div>
        <Select
          value={driverFilter || NONE}
          onValueChange={(v) => setDriverFilter(v === NONE ? '' : v)}
        >
          <SelectTrigger className="w-[220px]">
            <SelectValue placeholder="All drivers" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>All drivers</SelectItem>
            {drivers.map((d) => (
              <SelectItem key={d.id} value={d.id}>{d.title}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {hasReviewSignal && (
          <button
            type="button"
            onClick={() => setOutcomeReviewFilter(!outcomeReviewFilter)}
            aria-pressed={outcomeReviewFilter}
            className={cn(
              'inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium transition-colors',
              outcomeReviewFilter
                ? 'border-amber-500/60 bg-amber-500/15 text-amber-700 dark:text-amber-400'
                : 'border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground',
            )}
          >
            Outcome review due{outcomeReviewFilter ? ' ✓' : ''}
          </button>
        )}
      </div>

      {error && (
        <Alert variant="error" className="mb-6">
          <div className="flex items-start justify-between gap-3">
            <AlertDescription className="flex-1">{error}</AlertDescription>
            <button
              type="button"
              onClick={() => setError(null)}
              aria-label="Dismiss"
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </Alert>
      )}

      {loading && (
        <div className="py-10 text-center">
          <Loader2 className="mx-auto h-7 w-7 animate-spin text-muted-foreground" />
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <Card>
          <CardContent className="py-10 text-center">
            <Gavel className="mx-auto mb-3 h-12 w-12 text-muted-foreground/50" />
            <p className="text-base font-semibold text-muted-foreground">
              {decisions.length === 0 ? 'No decisions yet' : 'No decisions match your filters'}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              Use "Log decision" to capture a call you've made, with the meeting, stakeholders, and evidence that backed it.
            </p>
          </CardContent>
        </Card>
      )}

      {!loading && filtered.length > 0 && (
        <div className="flex flex-col gap-3">
          {filtered.map((d) => {
            const driver = d.strategicDriverId ? driverById.get(d.strategicDriverId) : undefined;
            const project = d.projectId ? projectById.get(d.projectId) : undefined;
            const isExpanded = expandedId === d.id;
            return (
              <Card key={d.id}>
                <CardContent>
                  <div className="flex justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-base font-semibold">{d.title}</p>
                        <PillChip>{prettyStatus(d.status)}</PillChip>
                        <PillChip>{formatDate(d.decisionDate)}</PillChip>
                        {d.isPending && (
                          <PillChip tone="warning">Pending</PillChip>
                        )}
                      </div>
                      <div className="mt-1.5 flex flex-wrap items-center gap-2">
                        {driver && <PillChip>Driver: {driver.title}</PillChip>}
                        {project && <PillChip>Project: {project.title}</PillChip>}
                        {d.meetingSource && <PillChip>{d.meetingSource}</PillChip>}
                      </div>
                      {isExpanded && (
                        <div className="mt-4 flex flex-col gap-3">
                          {d.rationale && <Meta label="Rationale" text={d.rationale} />}
                          {d.stakeholders && <Meta label="Stakeholders" text={d.stakeholders} />}
                          {d.evidence && <Meta label="Evidence" text={d.evidence} />}
                          <div className="flex flex-wrap gap-6">
                            <Meta label="Review date" text={formatDate(d.reviewDate)} />
                            <Meta label="Outcome review" text={formatDate(d.outcomeReviewDate)} />
                            {d.reviewReason && <Meta label="Review reason" text={d.reviewReason} />}
                          </div>
                          {d.resolution && (
                            <Meta
                              label={`Resolution (${formatDate(d.resolvedAt)})`}
                              text={d.resolutionNotes ? `${d.resolution} — ${d.resolutionNotes}` : d.resolution}
                            />
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Button
                        size="icon-sm"
                        variant="ghost"
                        onClick={() => handleOpenEdit(d)}
                        aria-label="Edit decision"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon-sm"
                        variant="ghost"
                        onClick={() => setExpandedId(isExpanded ? null : d.id)}
                        aria-label={isExpanded ? 'Collapse' : 'Expand'}
                        aria-expanded={isExpanded}
                      >
                        {isExpanded
                          ? <ChevronUp className="h-4 w-4" />
                          : <ChevronDown className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create / edit decision dialog */}
      <Dialog
        open={dialogOpen}
        onOpenChange={(o) => { if (!o && !saving) setDialogOpen(false); }}
      >
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{form.id ? 'Edit decision' : 'Log decision'}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="dec-title">Title <span className="text-destructive">*</span></Label>
              <Input
                id="dec-title"
                required
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="dec-rationale">Rationale</Label>
              <Textarea
                id="dec-rationale"
                rows={3}
                value={form.rationale}
                onChange={(e) => setForm({ ...form, rationale: e.target.value })}
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="dec-date">Decision date</Label>
                <Input
                  id="dec-date"
                  type="date"
                  value={form.decisionDate}
                  onChange={(e) => setForm({ ...form, decisionDate: e.target.value })}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="dec-status">Status</Label>
                <Select
                  value={form.status}
                  onValueChange={(v) => setForm({ ...form, status: v })}
                >
                  <SelectTrigger id="dec-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['Draft', 'Final', 'UnderReview', 'Revisited'].map((s) => (
                      <SelectItem key={s} value={s}>{prettyStatus(s)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="dec-stakeholders">Stakeholders</Label>
              <Input
                id="dec-stakeholders"
                placeholder="Comma-separated names"
                value={form.stakeholders}
                onChange={(e) => setForm({ ...form, stakeholders: e.target.value })}
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="dec-driver">Strategic driver</Label>
                <Select
                  value={form.strategicDriverId || NONE}
                  onValueChange={(v) => setForm({ ...form, strategicDriverId: v === NONE ? '' : v })}
                >
                  <SelectTrigger id="dec-driver"><SelectValue placeholder="No driver" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>No driver</SelectItem>
                    {drivers.map((d) => (
                      <SelectItem key={d.id} value={d.id}>{d.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="dec-project">Project</Label>
                <Select
                  value={form.projectId || NONE}
                  onValueChange={(v) => setForm({ ...form, projectId: v === NONE ? '' : v })}
                >
                  <SelectTrigger id="dec-project"><SelectValue placeholder="No project" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>No project</SelectItem>
                    {projects.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="dec-meeting">Meeting source</Label>
              <Input
                id="dec-meeting"
                placeholder="e.g. Q1 Leadership Sync, 18 Apr"
                value={form.meetingSource}
                onChange={(e) => setForm({ ...form, meetingSource: e.target.value })}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="dec-evidence">Evidence</Label>
              <Textarea
                id="dec-evidence"
                rows={4}
                placeholder="Links, documents, data points, or quotes — one per line."
                value={form.evidence}
                onChange={(e) => setForm({ ...form, evidence: e.target.value })}
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="dec-review-date">Review date</Label>
                <Input
                  id="dec-review-date"
                  type="date"
                  value={form.reviewDate}
                  onChange={(e) => setForm({ ...form, reviewDate: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">When to reconsider the decision itself</p>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="dec-outcome-review">Outcome review</Label>
                <Input
                  id="dec-outcome-review"
                  type="date"
                  value={form.outcomeReviewDate}
                  onChange={(e) => setForm({ ...form, outcomeReviewDate: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">When to check whether it was right</p>
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="dec-review-reason">Review reason</Label>
              <Input
                id="dec-review-reason"
                value={form.reviewReason}
                onChange={(e) => setForm({ ...form, reviewReason: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || !form.title.trim()}
              className="gap-1.5"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {saving ? 'Saving…' : form.id ? 'Save changes' : 'Log decision'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small label + body block used inside the expanded decision detail card.
// The label is a small uppercase-tracked muted line; the body is wrapped so
// pre-formatted text (rationale, evidence) preserves its line breaks.
// ---------------------------------------------------------------------------
function Meta({ label, text }: { label: string; text: string }) {
  return (
    <div>
      <p className="mb-0.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        {label}
      </p>
      <p className="whitespace-pre-wrap text-sm">{text}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Outlined pill for status / date / driver / project chips. Inline rather
// than reaching for the shadcn Badge primitive because the tone palette is
// limited to default + warning.
// ---------------------------------------------------------------------------
function PillChip({
  children,
  tone = 'default',
}: {
  children: React.ReactNode;
  tone?: 'default' | 'warning';
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border bg-transparent px-2 py-0.5 text-[11px] font-medium',
        tone === 'warning'
          ? 'border-amber-500/60 text-amber-700 dark:text-amber-400'
          : 'border-border text-muted-foreground',
      )}
    >
      {children}
    </span>
  );
}
